import {
    ChartColumnIcon,
    ChartLineIcon,
    ChartPieIcon,
    HashIcon,
    ListOrderedIcon,
    type SvgIconComponent,
    TargetIcon,
} from 'components/icons';
import type { HomeWidgetDefinition, HomeWidgetForm, HomeWidgetPeriod } from 'types/homeWidgets';
import { t, translations } from 'utils/getTranslations';
import { FAMILY_LIST_LABEL } from '../kpi/widgetLink';

export const FORM_LABEL: Record<HomeWidgetForm, string> = {
    NUMBER: 'home.widgets.creator.form.number',
    TREND: 'home.widgets.creator.form.trend',
    BREAKDOWN: 'home.widgets.creator.form.breakdown',
    RANKED: 'home.widgets.creator.form.ranked',
    PROGRESS: 'home.widgets.creator.form.progress',
};

export const FORM_ICON: Record<HomeWidgetForm, SvgIconComponent> = {
    NUMBER: HashIcon,
    TREND: ChartLineIcon,
    BREAKDOWN: ChartColumnIcon,
    RANKED: ListOrderedIcon,
    PROGRESS: TargetIcon,
};

/** The glyph of a widget's form; a breakdown drawn as a donut has its own. */
export function formIcon(definition: HomeWidgetDefinition): SvgIconComponent {
    return definition.source === 'CATALOGUE' && definition.drawnAs === 'DONUT'
        ? ChartPieIcon
        : FORM_ICON[definition.form];
}

/** The wording of a period: `THIS_MONTH` is `home.widgets.period.this-month`. */
export function periodLabel(period: HomeWidgetPeriod): string {
    return t(`home.widgets.period.${period.toLowerCase().replaceAll('_', '-')}`);
}

/** The catalogue names its families, measures and dimensions by code; one the copy does not know reads as its code. */
function labelOf(ids: string[], code: string): string {
    const id = ids.find((candidate) => translations[candidate] !== undefined);
    return id ? t(id) : code;
}

/** A family is named after its list. */
export function familyLabel(family: string): string {
    return labelOf([FAMILY_LIST_LABEL[family]], family);
}

/** A measure is worded per family where the same code counts different things, such as each family's `count`. */
export function measureLabel(family: string, measure: string): string {
    return labelOf([`home.widgets.measure.${family}.${measure}`, `home.widgets.measure.${measure}`], measure);
}

export function dimensionLabel(dimension: string): string {
    return labelOf([`home.widgets.dimension.${dimension}`], dimension);
}

/** What a widget is called until its author names it: its measure and its period. */
export function defaultName(family: string, measure: string, period: HomeWidgetPeriod): string {
    return t('home.widgets.creator.default-name', {
        measure: measureLabel(family, measure),
        period: periodLabel(period).toLocaleLowerCase(),
    });
}
